import type { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';

interface Props {
  editor: Editor;
  onInsertQuery: () => void;
  sourceMode: boolean;
  onToggleSource: () => void;
}

function Btn({ onClick, active, disabled, title, children }: { onClick: () => void; active?: boolean; disabled?: boolean; title: string; children: React.ReactNode }) {
  return (
    <button type="button" className={`tb-btn${active ? ' is-active' : ''}`} onMouseDown={e => e.preventDefault()} onClick={onClick} disabled={disabled} title={title} aria-label={title} aria-pressed={active}>
      {children}
    </button>
  );
}

export default function Toolbar({ editor, onInsertQuery, sourceMode, onToggleSource }: Props) {
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      heading: [1, 2, 3, 4, 5].find(l => e.isActive('heading', { level: l })) ?? 0,
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      strike: e.isActive('strike'),
      code: e.isActive('code'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      quote: e.isActive('blockquote'),
      codeBlock: e.isActive('codeBlock'),
      link: e.isActive('link'),
      table: e.isActive('table'),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });

  const chain = () => editor.chain().focus();

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const href = window.prompt('Link URL (absolute, or a wiki path like /Requirements/Overview)', prev ?? 'https://');
    if (href === null) return;
    if (!href.trim()) {
      chain().extendMarkRange('link').unsetLink().run();
      return;
    }
    chain().extendMarkRange('link').setLink({ href: href.trim() }).run();
  };

  const insertImage = () => {
    const src = window.prompt('Image URL or attachment path (e.g. /.attachments/diagram.png)');
    if (!src) return;
    chain().setImage({ src: src.trim() }).run();
  };

  const insertMermaid = () =>
    chain()
      .insertContent({ type: 'mermaid', content: [{ type: 'text', text: 'graph TD\n  A[Start] --> B{Decision}\n  B -->|Yes| C[Done]\n  B -->|No| A' }] })
      .run();

  return (
    <div className="toolbar" role="toolbar" aria-label="Formatting">
      <select
        className="tb-select"
        value={s.heading}
        disabled={sourceMode}
        onMouseDown={e => e.stopPropagation()}
        onChange={e => {
          const level = Number(e.target.value);
          if (level === 0) chain().setParagraph().run();
          else chain().setHeading({ level: level as 1 | 2 | 3 | 4 | 5 }).run();
        }}
        aria-label="Block type"
      >
        <option value={0}>Paragraph</option>
        <option value={1}>Heading 1</option>
        <option value={2}>Heading 2</option>
        <option value={3}>Heading 3</option>
        <option value={4}>Heading 4</option>
        <option value={5}>Heading 5</option>
      </select>

      <span className="tb-sep" />
      <Btn title="Bold (Ctrl+B)" active={s.bold} disabled={sourceMode} onClick={() => chain().toggleBold().run()}><b>B</b></Btn>
      <Btn title="Italic (Ctrl+I)" active={s.italic} disabled={sourceMode} onClick={() => chain().toggleItalic().run()}><i>I</i></Btn>
      <Btn title="Strikethrough" active={s.strike} disabled={sourceMode} onClick={() => chain().toggleStrike().run()}><s>S</s></Btn>
      <Btn title="Inline code" active={s.code} disabled={sourceMode} onClick={() => chain().toggleCode().run()}><code>{'<>'}</code></Btn>

      <span className="tb-sep" />
      <Btn title="Bulleted list" active={s.bullet} disabled={sourceMode} onClick={() => chain().toggleBulletList().run()}>• List</Btn>
      <Btn title="Numbered list" active={s.ordered} disabled={sourceMode} onClick={() => chain().toggleOrderedList().run()}>1. List</Btn>
      <Btn title="Block quote" active={s.quote} disabled={sourceMode} onClick={() => chain().toggleBlockquote().run()}>❝</Btn>
      <Btn title="Code block" active={s.codeBlock} disabled={sourceMode} onClick={() => chain().toggleCodeBlock().run()}>{'{ }'}</Btn>

      <span className="tb-sep" />
      <Btn title="Link" active={s.link} disabled={sourceMode} onClick={setLink}>🔗</Btn>
      <Btn title="Image" disabled={sourceMode} onClick={insertImage}>🖼</Btn>
      <Btn title="Table" active={s.table} disabled={sourceMode} onClick={() => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>▦</Btn>
      {s.table && (
        <span className="tb-group">
          <Btn title="Add row below" onClick={() => chain().addRowAfter().run()}>+Row</Btn>
          <Btn title="Add column after" onClick={() => chain().addColumnAfter().run()}>+Col</Btn>
          <Btn title="Delete row" onClick={() => chain().deleteRow().run()}>−Row</Btn>
          <Btn title="Delete column" onClick={() => chain().deleteColumn().run()}>−Col</Btn>
          <Btn title="Delete table" onClick={() => chain().deleteTable().run()}>✕ Table</Btn>
        </span>
      )}
      <Btn title="Mermaid diagram" disabled={sourceMode} onClick={insertMermaid}>Mermaid</Btn>

      <span className="tb-sep" />
      <Btn title="Table of contents [[_TOC_]]" disabled={sourceMode} onClick={() => chain().insertContent({ type: 'adoToc' }).run()}>TOC</Btn>
      <Btn title="Table of sub-pages [[_TOSP_]]" disabled={sourceMode} onClick={() => chain().insertContent({ type: 'adoTosp' }).run()}>Sub-pages</Btn>
      <Btn title="Embed query results" disabled={sourceMode} onClick={onInsertQuery}>Query</Btn>

      <span className="tb-sep" />
      <Btn title="Undo (Ctrl+Z)" disabled={sourceMode || !s.canUndo} onClick={() => chain().undo().run()}>↶</Btn>
      <Btn title="Redo (Ctrl+Y)" disabled={sourceMode || !s.canRedo} onClick={() => chain().redo().run()}>↷</Btn>

      <span className="spacer" />
      <Btn title={sourceMode ? 'Back to visual editor' : 'Edit markdown source'} active={sourceMode} onClick={onToggleSource}>
        {sourceMode ? 'Visual' : 'Markdown'}
      </Btn>
    </div>
  );
}
